import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Core colors */
    --color-bg-dark: #0A101E;  /* Deep navy blue */
    --color-bg-medium: #131C36; /* Medium navy */
    --color-bg-light: #1C2849;  /* Lighter navy */
    --color-accent-primary: #A68CFF; /* Soft purple */
    --color-accent-secondary: #FF9776; /* Warm peach */
    --color-accent-tertiary: #4FACFE; /* Bright blue */
    --color-text-primary: #FFFFFF;
    --color-text-secondary: rgba(255, 255, 255, 0.75);
    --color-text-tertiary: rgba(255, 255, 255, 0.5);
    
    /* Typography */
    --font-heading: 'freight-display-pro', Georgia, serif;
    --font-body: 'Jost', sans-serif;
    --font-size-base: 16px;
    --line-height-base: 1.6;
    
    /* Spacing */
    --spacing-xs: 0.5rem;
    --spacing-sm: 1rem;
    --spacing-md: 2rem;
    --spacing-lg: 4rem;
    --spacing-xl: 8rem;
    
    /* Animations */
    --transition-fast: 0.2s ease;
    --transition-medium: 0.4s ease;
    --transition-slow: 0.8s ease;
    
    /* Border Radius */
    --radius-sm: 4px;
    --radius-md: 8px;
    --radius-lg: 16px;
    --radius-xl: 24px;
    --radius-full: 9999px;
  }

  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: var(--font-size-base);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: var(--font-body);
    line-height: var(--line-height-base);
    background-color: var(--color-bg-dark);
    color: var(--color-text-primary);
    overflow-x: hidden;
    cursor: none;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-heading);
    font-weight: 500;
    line-height: 1.2;
    margin-bottom: var(--spacing-sm);
  }

  h1 {
    font-size: 3.815rem;
    font-weight: 600;
  }

  h2 {
    font-size: 3.052rem;
  }

  h3 {
    font-size: 2.441rem;
  }

  h4 {
    font-size: 1.953rem;
  }

  h5 {
    font-size: 1.563rem;
  }

  h6 {
    font-size: 1.25rem;
  }

  p {
    margin-bottom: var(--spacing-sm);
    font-size: 1.125rem;
    font-weight: 300;
  }

  a {
    color: var(--color-accent-primary);
    text-decoration: none;
    transition: all var(--transition-fast);
    
    &:hover {
      color: var(--color-accent-secondary);
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  button {
    cursor: pointer;
    font-family: var(--font-body);
    font-size: 1rem;
    background: none;
    border: none;
    outline: none;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  /* Custom cursor styles */
  body.has-custom-cursor {
    cursor: none !important;
  }

  #cursor {
    position: fixed;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(166, 140, 255, 0.2);
    backdrop-filter: blur(4px);
    pointer-events: none;
    z-index: 9999;
    mix-blend-mode: difference;
    transition: width 0.2s, height 0.2s, background-color 0.2s;
    transform: translate(-50%, -50%);
    display: flex;
    justify-content: center;
    align-items: center;
    
    &::after {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--color-accent-primary);
    }
  }

  /* Utility Classes */
  .container {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 var(--spacing-sm);
  }

  .section {
    padding: var(--spacing-lg) 0;
  }

  .text-center {
    text-align: center;
  }

  /* Accessibility */
  *:focus-visible {
    outline: 2px solid var(--color-accent-primary);
    outline-offset: 2px;
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }

  /* Media Queries */
  @media (max-width: 1200px) {
    h1 { font-size: 3.052rem; }
    h2 { font-size: 2.441rem; }
    h3 { font-size: 1.953rem; }
    h4 { font-size: 1.563rem; }
    h5 { font-size: 1.25rem; }
    h6 { font-size: 1.125rem; }
  }

  @media (max-width: 768px) {
    h1 { font-size: 2.441rem; }
    h2 { font-size: 1.953rem; }
    h3 { font-size: 1.563rem; }
    h4 { font-size: 1.25rem; }
    h5 { font-size: 1.125rem; }
    h6 { font-size: 1rem; }
    
    .section {
      padding: var(--spacing-md) 0;
    }
  }
`;

export default GlobalStyles;